import { useCallback, useEffect, useRef, useState } from 'react';
import diffLatest from './diffLatest';
import makeGraph from './graph/makeGraph';
import mutate from './mutate';

/**
 * @param {Object} snapshot
 * @param {Function} onMutate
 * @param {Boolean} isHistorical
 * @returns {Array}
 */
export default function useGraphState({
  snapshot,
  isHistorical,
  activities,
  conditions,
  library,
}) {
  const [state, setState] = useState(null);
  const ref = useRef({});

  /**
   * Handle snapshot updates, including order-only replacements whose event
   * and entity timestamps have not changed.
   */
  useEffect(() => {
    if (!snapshot) {
      ref.current.hash = undefined;
      setState(null);
      return;
    }

    // The graph's __hash is a local render token; compare received source hashes.
    if (state && snapshot.__hash === ref.current.hash) return;
    ref.current.hash = snapshot.__hash;

    if (!state) {
      console.debug('Snapshots.useGraphState: Setting initial state');
      handleState(snapshot);
      return;
    }

    if (isHistorical) {
      handleState(snapshot);
      return;
    }

    const diff = diffLatest(state, snapshot);
    if (!diff) return;

    console.debug('Snapshots.useGraphState: Patching received diff', diff);
    const mutated = mutate(state, diff);
    handleState(mutated);
  }, [snapshot?.__hash, isHistorical]);

  /**
   * Handle activity state changes
   */
  useEffect(() => {
    ref.current.activities = activities;
    if (state && state._activities !== activities) {
      const next = state.clone();
      next._activities = activities;
      setState(next);
    }
  }, [activities]);

  /**
   * Handle condition changes
   */
  useEffect(() => {
    ref.current.conditions = conditions;
    if (state && state._conditions !== conditions) {
      const next = state.clone();
      next._conditions = conditions;
      setState(next);
    }
  }, [conditions]);

  /**
   * Handle library changes
   */
  useEffect(() => {
    ref.current.library = library;
    if (state && state._library !== library) {
      const next = state.clone();
      next._library = library;
      setState(next);
    }
  }, [library]);

  /**
   * Make and set a graph state from given snapshot
   */
  const handleState = (snapshot) => {
    const { activities, conditions, library } = ref.current;
    const graph = makeGraph(snapshot, { activities, conditions, library });
    setState(graph);
    return graph;
  };

  return [state, useCallback(handleState, [])];
}
